import React, { Children, useState } from "react";
import { useCollapsedState } from "./hooks";
import classNames from "classnames";

interface Props {
  title?: JSX.Element | string
  uniqueRowId: string
  collapsible: "no" | "startOpen" | "startClosed"
  type?: "regular" | "form";
  style?: "regular" | "white";
  disabled?: boolean
}

const CollapsibleSection = (props: React.PropsWithChildren<Props>) => {
  const { collapsed, toggleCollapsed } = useCollapsedState(props.uniqueRowId, props.collapsible === "startClosed");
  const [closingSection, setClosingSection] = useState(false);
  const [openingSection, setOpeningSection] = useState(false);

  const onToggle = () => {
    if (props.disabled) {
      return;
    }

    if (collapsed) {
      open();
    } else {
      close();
    }
  };

  const open = () => {
    if (closingSection || openingSection) {
      return;
    }

    setOpeningSection(true);
    setTimeout(() => {
      setOpeningSection(false);
      toggleCollapsed();
    }, 300);
  };

  const close = () => {
    if (closingSection || openingSection) {
      return;
    }

    setClosingSection(true);
    setTimeout(() => {
      setClosingSection(false);
      toggleCollapsed();
    }, 300);
  };

  if (
    Children.count(props.children) === 0 || // No children
    Children.toArray(props.children).every(child => !child) // Or all children are falsy (e.g. null)
    // This will not detect empty fragments
  ) return null;

  const collapsibleSectionClasses = classNames("collapsible-section", {
    white: props.style === "white",
    regular: props.style !== "white",
    collapsed: collapsed && !closingSection && !openingSection,
    open: !collapsed && !openingSection && !closingSection,
    opening: openingSection,
    collapsing: closingSection
  });

  // If we are collapsed, we want the whole div clickable to make it easier to open
  if (props.collapsible !== "no") {
    return (<div className={collapsibleSectionClasses} onClick={collapsed ? onToggle : null} id={props.uniqueRowId}>
      <div className="collapsible-section-open-close" onClick={collapsed ? null : onToggle}>
        <div className="flex">
          <div className="grow1 collapsible-section-header">{props.title}</div>
          <div>
            {!props.disabled && <i className="collapsible-section-icon icon-angle-down" />}
          </div>
        </div>
      </div>
      <div className="collapsible-section-content">
        <div className={classNames("collapsible-section-content-inner", {
          open: !collapsed && !closingSection && !openingSection
        })}>
          {props.children}
        </div>
      </div>
    </div>);
  }

  const contentSectionClasses = classNames("content-section", {
    "has-form-elements": props.type === "form"
  });

  return (<div className={contentSectionClasses} id={props.uniqueRowId}>
    {(props.title) && <div className="content-section-header">
      {props.title}
    </div>}
    <div className="content-section-body">
      {props.children}
    </div>
  </div>);
};

export default CollapsibleSection;